import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { MoreHorizontal } from "lucide-react";
import React, { useState } from "react";
import { strings } from "@/I18n";
import { useIsMobile } from "@/v2/hooks/useMediaQuery";
import { Button } from "@/v2/ui/primitives/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/v2/ui/primitives/dropdown-menu";
import { Sheet, SheetContent, SheetTitle } from "@/v2/ui/primitives/sheet";

interface TableActionsButtonProps {
  children: React.ReactNode;
  align?: "start" | "end" | "center";
  /** Title shown in the mobile bottom sheet header */
  sheetTitle?: string;
}

export function TableActionsButton({
  children,
  align = "end",
  sheetTitle = strings("common.actions"),
}: TableActionsButtonProps) {
  const isMobile = useIsMobile();
  const [sheetOpen, setSheetOpen] = useState(false);

  // Recursively process children to transform MenuItems to buttons
  const processChildren = (nodes: React.ReactNode): React.ReactNode => {
    return React.Children.map(nodes, (child) => {
      if (!React.isValidElement(child)) return child;

      // Handle Fragments
      if (child.type === React.Fragment) {
        return processChildren(child.props.children);
      }

      const childType = child.type as React.ComponentType & {
        displayName?: string;
        name?: string;
      };
      const componentName =
        childType.displayName ||
        childType.name ||
        (typeof child.type === "string" ? child.type : "");

      if (
        componentName.includes("DropdownMenuSeparator") ||
        componentName.includes("Separator")
      ) {
        return (
          <div
            key={child.key}
            className="tw-h-px tw-bg-border tw-my-2 tw-mx-4"
          />
        );
      }

      if (
        componentName.includes("MenuItem") ||
        componentName.includes("Item")
      ) {
        const {
          className,
          children: itemChildren,
          disabled,
          onClick,
        } = child.props as {
          className?: string;
          children?: React.ReactNode;
          disabled?: boolean;
          onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
        };
        return (
          <button
            key={child.key}
            type="button"
            disabled={disabled}
            onClick={(e) => {
              if (onClick) onClick(e);
              setSheetOpen(false);
            }}
            className={`tw-flex tw-items-center tw-gap-3 tw-px-6 tw-py-4 tw-text-left tw-text-base tw-transition-colors hover:tw-bg-accent active:tw-bg-accent disabled:tw-opacity-50 disabled:tw-pointer-events-none ${className || ""}`}
          >
            {itemChildren}
          </button>
        );
      }

      // If it has its own children, we might need to process them if it's just a wrapper
      if (child.props && child.props.children && !componentName) {
        return processChildren(child.props.children);
      }

      return child;
    });
  };

  // On mobile, use a bottom sheet for better touch UX
  if (isMobile) {
    return (
      <>
        <Button
          variant="outline"
          size="sm"
          className="tw-h-8 tw-px-2 tw-cursor-pointer"
          onClick={() => setSheetOpen(true)}
        >
          <MoreHorizontal className="tw-h-4 tw-w-4" />
        </Button>
        {sheetOpen && (
          <div
            className="tw-fixed tw-inset-0 tw-z-40 tw-bg-black/40 tw-animate-in tw-fade-in-0 tw-duration-200"
            onClick={() => setSheetOpen(false)}
          />
        )}
        <Sheet open={sheetOpen} onOpenChange={setSheetOpen} modal={false}>
          <SheetContent
            side="bottom"
            className="tw-rounded-t-xl tw-px-0 tw-pb-8 tw-pt-4 tw-max-h-[85vh] tw-z-50"
          >
            <VisuallyHidden>
              <SheetTitle>{sheetTitle}</SheetTitle>
            </VisuallyHidden>
            <div className="tw-mx-auto tw-mb-4 tw-h-1.5 tw-w-12 tw-rounded-full tw-bg-muted" />
            <div
              className="tw-flex tw-flex-col tw-overflow-y-auto"
              onClick={() => setSheetOpen(false)}
            >
              {processChildren(children)}
            </div>
          </SheetContent>
        </Sheet>
      </>
    );
  }

  // On desktop, use Radix DropdownMenu for proper context
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild={true}>
        <Button
          variant="outline"
          size="sm"
          className="tw-h-8 tw-px-2 tw-cursor-pointer"
        >
          <MoreHorizontal className="tw-h-4 tw-w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align}>{children}</DropdownMenuContent>
    </DropdownMenu>
  );
}
